/**
 * TEI (Technology Enhanced Items) Question Type Inference
 * 
 * SINGLE SOURCE OF TRUTH for determining question rendering type.
 * This function must be used by ChallengePage.jsx to decide which
 * TEI component to render.
 */

/**
 * Infer the question type from the question object.
 * 
 * PRIORITY ORDER:
 * 1. Explicit question_type field (if present and valid)
 * 2. Data-shape inference (matching pairs, fill blank patterns)
 * 3. Default to 'multiple_choice'
 * 
 * @param {Object} question - The question object from the API
 * @returns {string} - One of: 'multiple_choice', 'multi_select', 'fill_blank', 'matching'
 */
export function inferQuestionType(question) {
  if (!question) {
    console.log('[TEI_RENDERER] No question provided, defaulting to multiple_choice');
    return 'multiple_choice';
  }

  // 1. Check explicit question_type field first (highest priority)
  const explicitType = question.question_type;
  if (explicitType) {
    const normalizedType = normalizeQuestionType(explicitType);
    if (isValidTEIType(normalizedType)) {
      console.log(`[TEI_RENDERER] Using explicit question_type: ${normalizedType}`);
      return normalizedType;
    }
  }

  // 2. Check for matching question (has pairs or matching_pairs)
  if (hasMatchingData(question)) {
    console.log('[TEI_RENDERER] Detected matching question from data shape');
    return 'matching';
  }

  // 3. Check for fill-in-the-blank (has ___ pattern in question_text)
  if (hasFillBlankPattern(question)) {
    console.log('[TEI_RENDERER] Detected fill_blank question from ___ pattern');
    return 'fill_blank';
  }

  // 4. Check for multi-select (has is_multi_select flag or multiple correct answers)
  if (hasMultiSelectIndicators(question)) {
    console.log('[TEI_RENDERER] Detected multi_select question from indicators');
    return 'multi_select';
  }

  // 5. Default to multiple choice
  console.log('[TEI_RENDERER] Defaulting to multiple_choice');
  return 'multiple_choice';
}

/**
 * Normalize question type string to standard format
 */
function normalizeQuestionType(type) {
  if (!type) return null;
  
  const normalized = type.toLowerCase().trim().replace(/-/g, '_');
  
  // Map various formats to standard types
  const typeMap = {
    'multiple_choice': 'multiple_choice',
    'multiplechoice': 'multiple_choice',
    'mc': 'multiple_choice',
    'standard': 'multiple_choice',
    'multi_select': 'multi_select',
    'multiselect': 'multi_select',
    'select_all': 'multi_select',
    'selectall': 'multi_select',
    'fill_blank': 'fill_blank',
    'fillblank': 'fill_blank',
    'fill_in_blank': 'fill_blank',
    'fill_in_the_blank': 'fill_blank',
    'fib': 'fill_blank',
    'matching': 'matching',
    'match': 'matching',
    'pairs': 'matching'
  };
  
  return typeMap[normalized] || normalized;
}

/**
 * Check if the type is a valid TEI type
 */
function isValidTEIType(type) {
  return ['multiple_choice', 'multi_select', 'fill_blank', 'matching'].includes(type);
}

/**
 * Check if question has matching data structure
 */
function hasMatchingData(question) {
  // Check for pairs array
  if (Array.isArray(question.pairs) && question.pairs.length > 0) {
    return true;
  }
  
  // Check for matching_pairs array
  if (Array.isArray(question.matching_pairs) && question.matching_pairs.length > 0) {
    return true;
  }
  
  // Check for left/right columns
  if (Array.isArray(question.left_column) && Array.isArray(question.right_column)) {
    return true;
  }
  
  return false;
}

/**
 * Check if question has fill-in-the-blank pattern
 */
function hasFillBlankPattern(question) {
  const text = question.question_text || '';
  
  // Check for ___ pattern (3+ underscores)
  if (/_{3,}/.test(text)) {
    return true;
  }
  
  // Check for [blank] pattern
  if (/\[blank\]/i.test(text)) {
    return true;
  }
  
  // Check for {blank} pattern
  if (/\{blank\}/i.test(text)) {
    return true;
  }
  
  return false;
}

/**
 * Check if question has multi-select indicators
 */
function hasMultiSelectIndicators(question) {
  // Check for explicit flag
  if (question.is_multi_select === true) {
    return true;
  }
  
  // Check for multiple correct answers array
  if (Array.isArray(question.correct_options) && question.correct_options.length > 1) {
    return true;
  }
  
  // Check question text for multi-select keywords
  const text = (question.question_text || '').toLowerCase();
  const multiSelectKeywords = [
    'select all',
    'choose all',
    'which of the following are',
    'which answers are',
    'select each',
    'check all',
    'mark all'
  ];
  
  return multiSelectKeywords.some(keyword => text.includes(keyword));
}

export default inferQuestionType;
